import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Award, Download, ArrowLeft, Loader2, Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-wellness-spot/artifacts/y6uqcyxn_1683577696901.png";

const translations = {
  en: {
    certificateOf: 'Certificate of Completion',
    certifies: 'This certifies that',
    completed: 'has successfully completed the training',
    hours: 'hours of training',
    score: 'Final score',
    issued: 'Issued on',
    certNumber: 'Certificate N°',
    director: 'Training Director',
    download: 'Download Certificate',
    back: 'Back to my courses',
    share: 'Share',
    notFound: 'Certificate not found'
  },
  fr: {
    certificateOf: "Certificat d'Accomplissement",
    certifies: 'Ce certificat atteste que',
    completed: 'a complété avec succès la formation',
    hours: 'heures de formation',
    score: 'Score final',
    issued: 'Délivré le',
    certNumber: 'Certificat N°',
    director: 'Directrice de Formation',
    download: 'Télécharger le Certificat',
    back: 'Retour à mes formations',
    share: 'Partager',
    notFound: 'Certificat non trouvé'
  },
  pt: {
    certificateOf: 'Certificado de Conclusão',
    certifies: 'Este certificado atesta que',
    completed: 'concluiu com sucesso a formação',
    hours: 'horas de formação',
    score: 'Pontuação final',
    issued: 'Emitido em',
    certNumber: 'Certificado N°',
    director: 'Diretora de Formação',
    download: 'Descarregar Certificado',
    back: 'Voltar às minhas formações',
    share: 'Partilhar',
    notFound: 'Certificado não encontrado'
  },
  de: {
    certificateOf: 'Abschlusszertifikat',
    certifies: 'Dieses Zertifikat bescheinigt, dass',
    completed: 'die Schulung erfolgreich abgeschlossen hat',
    hours: 'Schulungsstunden',
    score: 'Endergebnis',
    issued: 'Ausgestellt am',
    certNumber: 'Zertifikat Nr.',
    director: 'Schulungsleiterin',
    download: 'Zertifikat Herunterladen',
    back: 'Zurück zu meinen Kursen',
    share: 'Teilen',
    notFound: 'Zertifikat nicht gefunden'
  },
  es: {
    certificateOf: 'Certificado de Finalización',
    certifies: 'Este certificado acredita que',
    completed: 'ha completado exitosamente la formación',
    hours: 'horas de formación',
    score: 'Puntuación final',
    issued: 'Emitido el',
    certNumber: 'Certificado N°',
    director: 'Directora de Formación',
    download: 'Descargar Certificado',
    back: 'Volver a mis cursos',
    share: 'Compartir',
    notFound: 'Certificado no encontrado'
  }
};

const CertificatePage = () => {
  const { certificateId } = useParams();
  const { language } = useLanguage();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const t = translations[language] || translations.en;
  const certificateRef = useRef(null);

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchCertificate();
    }
  }, [certificateId, isAuthenticated, authLoading]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(
        `${API}/api/elearning/certificates/${certificateId}`,
        { withCredentials: true }
      );
      setCertificate(response.data);
    } catch (error) {
      console.error('Error fetching certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!certificateRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `IPRBC-KAMIT-Certificate-${certificate.certificate_number}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading certificate:', error);
    } finally {
      setDownloading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-kamit-cream flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-kamit-gold animate-spin" />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-kamit-cream flex items-center justify-center">
        <div className="text-center">
          <Award className="h-16 w-16 text-kamit-brown/30 mx-auto mb-4" />
          <h2 className="text-xl text-kamit-brown mb-4">{t.notFound}</h2>
          <Link to="/my-courses">
            <Button className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full">
              {t.back}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const issuedDate = new Date(certificate.issued_at).toLocaleDateString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-kamit-cream to-kamit-beige py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Link to="/my-courses" className="inline-flex items-center text-kamit-brown hover:text-kamit-gold mb-8">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t.back}
        </Link>

        {/* Certificate */}
        <div className="max-w-4xl mx-auto">
          <div 
            ref={certificateRef}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            style={{ aspectRatio: '1.414' }}
          >
            {/* Certificate Content */}
            <div className="relative h-full p-12 flex flex-col">
              {/* Decorative Border */}
              <div className="absolute inset-4 border-4 border-kamit-gold/30 rounded-2xl pointer-events-none"></div>
              <div className="absolute inset-6 border border-kamit-gold/20 rounded-xl pointer-events-none"></div>

              {/* Header */}
              <div className="text-center mb-8 relative z-10">
                <img src={LOGO_URL} alt="IPRBC KAMIT" className="h-24 mx-auto mb-4" />
                <h1 className="font-heading text-4xl text-kamit-brown mb-2">IPRBC KAMIT</h1>
                <p className="text-kamit-gold italic">
                  Instituts Professionnels de Relaxations et de Beautés Corporelles
                </p>
              </div>

              {/* Certificate Title */}
              <div className="text-center mb-8 relative z-10">
                <div className="inline-flex items-center gap-3 bg-kamit-gold/10 px-6 py-3 rounded-full">
                  <Award className="h-6 w-6 text-kamit-gold" />
                  <span className="font-heading text-2xl text-kamit-brown">{t.certificateOf}</span>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 flex flex-col justify-center text-center relative z-10">
                <p className="text-kamit-brown/70 text-lg mb-2">{t.certifies}</p>
                <h2 className="font-heading text-4xl text-kamit-brown mb-4">
                  {certificate.user_name}
                </h2>
                <p className="text-kamit-brown/70 text-lg mb-2">{t.completed}</p>
                <h3 className="font-heading text-2xl text-kamit-gold mb-6">
                  {certificate.course_title || certificate.certificate_title}
                </h3>

                {/* Stats */}
                <div className="flex justify-center gap-12 mb-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-kamit-brown">{certificate.total_hours}</p>
                    <p className="text-kamit-brown/60 text-sm">{t.hours}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-kamit-gold">{Math.round(certificate.final_score)}%</p>
                    <p className="text-kamit-brown/60 text-sm">{t.score}</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-end relative z-10">
                <div>
                  <p className="text-kamit-brown/60 text-sm">{t.issued}</p>
                  <p className="text-kamit-brown font-medium">{issuedDate}</p>
                  <p className="text-kamit-brown/60 text-xs mt-1">
                    {t.certNumber}: {certificate.certificate_number}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-32 border-t-2 border-kamit-brown/30 mb-2"></div>
                  <p className="text-kamit-brown font-medium">Esther Tchomba</p>
                  <p className="text-kamit-brown/60 text-sm">{t.director}</p>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute top-8 left-8 w-20 h-20 border-t-4 border-l-4 border-kamit-gold/40 rounded-tl-3xl"></div>
              <div className="absolute top-8 right-8 w-20 h-20 border-t-4 border-r-4 border-kamit-gold/40 rounded-tr-3xl"></div>
              <div className="absolute bottom-8 left-8 w-20 h-20 border-b-4 border-l-4 border-kamit-gold/40 rounded-bl-3xl"></div>
              <div className="absolute bottom-8 right-8 w-20 h-20 border-b-4 border-r-4 border-kamit-gold/40 rounded-br-3xl"></div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full px-8"
            >
              {downloading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Download className="h-5 w-5 mr-2" />
              )}
              {t.download}
            </Button>
            <Button
              variant="outline"
              className="border-kamit-brown text-kamit-brown hover:bg-kamit-brown hover:text-kamit-beige rounded-full px-8"
            >
              <Share2 className="h-5 w-5 mr-2" />
              {t.share}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
